import React from 'react';
import EmployeeSearch from './EmployeeSearch';
import SearchableSelect from './SearchableSelect';

export default function FormField({
  label,
  type = 'text',
  name,
  value,
  onChange,
  error,
  required = false,
  options = [],
  placeholder = '',
  disabled = false,
  min,
  max,
  step,
  rows = 3
}) {
  function handleChange(e) {
    const val = type === 'number' ? e.target.value : e.target.value;
    onChange(name, val);
  }

  function renderInput() {
    const baseProps = {
      id: name,
      name,
      className: `form-control${error ? ' error' : ''}`,
      disabled,
      placeholder
    };

    if (type === 'employee-search') {
      return (
        <EmployeeSearch
          options={options}
          value={value || ''}
          onChange={onChange}
          name={name}
          placeholder={placeholder || `Search ${label}...`}
          disabled={disabled}
        />
      );
    }

    if (type === 'select') {
      // Modern searchable dropdown — replaces the native <select>. Falls back
      // to native rendering if the caller passed `type="native-select"` so any
      // edge case that genuinely needs the native control can opt out.
      const normalised = options.map((opt) => {
        if (opt && typeof opt === 'object') return opt;
        return { value: opt, label: opt };
      });
      return (
        <SearchableSelect
          value={value || ''}
          onChange={(v) => onChange(name, v)}
          options={normalised}
          placeholder={placeholder || `Select ${label}...`}
          disabled={disabled}
          required={required}
          error={!!error}
          sort={false}
        />
      );
    }
    if (type === 'native-select') {
      return (
        <select {...baseProps} value={value || ''} onChange={handleChange}>
          <option value="">{placeholder || `Select ${label}...`}</option>
          {options.map(opt => {
            const optVal = typeof opt === 'object' ? opt.value : opt;
            const optLabel = typeof opt === 'object' ? opt.label : opt;
            return (
              <option key={optVal} value={optVal}>{optLabel}</option>
            );
          })}
        </select>
      );
    }

    if (type === 'textarea') {
      return (
        <textarea
          {...baseProps}
          value={value || ''}
          onChange={handleChange}
          rows={rows}
        />
      );
    }

    // type='currency' or type='indian-number' — show the value with Indian
    // grouping separators (1,00,000 not 1,00000) while storing the raw number
    // in state. The user types digits; we format on display, parse on change.
    // Rule of thumb: prefer this over <input type="number"> for any field
    // representing money or quantity in the Indian context.
    if (type === 'currency' || type === 'indian-number') {
      const raw = value === null || value === undefined ? '' : String(value);
      const numeric = raw.replace(/[^0-9.\-]/g, '');
      const display = (() => {
        if (numeric === '' || numeric === '-' || numeric === '.') return numeric;
        const n = Number(numeric);
        if (!isFinite(n)) return numeric;
        // Preserve any trailing decimal the user is mid-typing (e.g. "12.")
        const dotMatch = numeric.match(/\.(\d*)$/);
        const opts = dotMatch
          ? { minimumFractionDigits: dotMatch[1].length, maximumFractionDigits: dotMatch[1].length }
          : { maximumFractionDigits: 2 };
        return n.toLocaleString('en-IN', opts);
      })();
      return (
        <input
          {...baseProps}
          type="text"
          inputMode="decimal"
          value={display}
          onChange={(e) => {
            // Strip commas before bubbling up — parent state stays as a clean
            // number string ("1500000"), display layer adds the commas.
            const stripped = e.target.value.replace(/,/g, '');
            // Guard: only digits, one optional dot, optional leading minus.
            if (stripped !== '' && !/^-?\d*\.?\d*$/.test(stripped)) return;
            if (onChange) onChange(name, stripped);
          }}
        />
      );
    }

    return (
      <input
        {...baseProps}
        type={type}
        value={value || ''}
        onChange={handleChange}
        min={min}
        max={max}
        step={step}
      />
    );
  }

  return (
    <div className="form-group">
      {label && (
        <label className="form-label" htmlFor={name}>
          {label}
          {required && <span className="required">*</span>}
        </label>
      )}
      {renderInput()}
      {error && <div className="form-error">{error}</div>}
    </div>
  );
}
